import { Partner } from "@dashboard/common/types";
import {
  DEFAULT_CLOSE_TIME,
  DEFAULT_OPEN_TIME,
  DayScheduleEntry,
  HOLIDAY_DAY_OF_WEEK,
  StoreHoursFormState,
  WEEK_DAYS,
} from "../types/store-hours.types";

const parseTimeToMinutes = (time: string): number => {
  const [hourStr, minuteStr] = time.split(":");
  const hour = Number(hourStr) || 0;
  const minute = Number(minuteStr) || 0;
  return hour * 60 + minute;
};

const normalizeTime = (time?: string): string | null => {
  if (!time) {
    return null;
  }
  const parts = time.split(":");
  if (parts.length < 2) {
    return null;
  }
  const hour = parts[0].padStart(2, "0");
  const minute = parts[1].padStart(2, "0");
  return `${hour}:${minute}`;
};

type ScheduleDayConfig = {
  dayOfWeek: number;
  openTime: string;
  closeTime: string;
};

const findDayConfig = (
  scheduleDays: ScheduleDayConfig[] | undefined,
  dayOfWeek: number
) => scheduleDays?.find((item) => item.dayOfWeek === dayOfWeek);

export const cleanResume = (resume?: string): string | null => {
  if (!resume) {
    return null;
  }
  let clean = resume.trim();
  if (!clean) {
    return null;
  }
  if (clean.endsWith("|")) {
    clean = clean.substring(0, clean.length - 1);
  }
  return clean || null;
};

export const isValidTimeRange = (openTime: string, closeTime: string): boolean => {
  return parseTimeToMinutes(closeTime) > parseTimeToMinutes(openTime);
};

export const hasConfiguredStoreHours = (
  scheduleConfiguration?: Partner["scheduleConfiguration"]
): boolean => {
  const days = scheduleConfiguration?.scheduleDaysConfiguration || [];
  return days.some(
    (day) =>
      day.openTime &&
      day.closeTime &&
      isValidTimeRange(
        normalizeTime(day.openTime) || "",
        normalizeTime(day.closeTime) || ""
      )
  );
};

const mapDayEntry = (
  dayOfWeek: number,
  scheduleConfiguration?: Partner["scheduleConfiguration"]
): DayScheduleEntry => {
  const config = findDayConfig(
    scheduleConfiguration?.scheduleDaysConfiguration,
    dayOfWeek
  );
  const openTime = normalizeTime(config?.openTime);
  const closeTime = normalizeTime(config?.closeTime);

  return {
    dayOfWeek,
    enabled: Boolean(openTime && closeTime),
    openTime: openTime || DEFAULT_OPEN_TIME,
    closeTime: closeTime || DEFAULT_CLOSE_TIME,
  };
};

export const hydrateStoreHoursFromPartner = (
  partner?: Partner
): StoreHoursFormState => {
  const scheduleConfiguration = partner?.scheduleConfiguration;
  const holidayConfig = findDayConfig(
    scheduleConfiguration?.scheduleDaysConfiguration,
    HOLIDAY_DAY_OF_WEEK
  );
  const holidayOpen = normalizeTime(holidayConfig?.openTime);
  const holidayClose = normalizeTime(holidayConfig?.closeTime);

  const days = WEEK_DAYS.map((day) => mapDayEntry(day, scheduleConfiguration));
  const activeDays = days.filter((day) => day.enabled);
  const allDaysSameHour =
    activeDays.length > 1 &&
    activeDays.every(
      (day) =>
        day.openTime === activeDays[0].openTime &&
        day.closeTime === activeDays[0].closeTime
    );

  return {
    days,
    holiday: {
      enabled: Boolean(holidayOpen && holidayClose),
      openTime: holidayOpen || DEFAULT_OPEN_TIME,
      closeTime: holidayClose || DEFAULT_CLOSE_TIME,
    },
    allDaysSameHour,
    resume: scheduleConfiguration?.resume,
  };
};

export const buildScheduleConfigurationFromState = (
  state: StoreHoursFormState
): NonNullable<Partner["scheduleConfiguration"]> => {
  const scheduleDaysConfiguration = state.days
    .filter((day) => day.enabled)
    .map((day) => ({
      dayOfWeek: day.dayOfWeek,
      openTime: day.openTime,
      closeTime: day.closeTime,
    }));

  if (state.holiday.enabled) {
    scheduleDaysConfiguration.push({
      dayOfWeek: HOLIDAY_DAY_OF_WEEK,
      openTime: state.holiday.openTime,
      closeTime: state.holiday.closeTime,
    });
  }

  return {
    activeHolidays: state.holiday.enabled,
    resume: state.resume || "",
    scheduleDaysConfiguration,
  };
};

export const validateStoreHoursState = (state: StoreHoursFormState): boolean => {
  const entries = [
    ...state.days.filter((day) => day.enabled),
    ...(state.holiday.enabled
      ? [
          {
            dayOfWeek: HOLIDAY_DAY_OF_WEEK,
            enabled: true,
            openTime: state.holiday.openTime,
            closeTime: state.holiday.closeTime,
          },
        ]
      : []),
  ];

  if (!entries.length) {
    return true;
  }

  return entries.every((entry) =>
    isValidTimeRange(entry.openTime, entry.closeTime)
  );
};

export const applySameHourToAllEnabledDays = (
  days: DayScheduleEntry[]
): DayScheduleEntry[] => {
  const firstEnabled = days.find((day) => day.enabled);
  if (!firstEnabled) {
    return days;
  }

  return days.map((day) =>
    day.enabled
      ? {
          ...day,
          openTime: firstEnabled.openTime,
          closeTime: firstEnabled.closeTime,
        }
      : day
  );
};

export const toggleDayEnabled = (
  days: DayScheduleEntry[],
  dayOfWeek: number,
  allDaysSameHour: boolean
): DayScheduleEntry[] => {
  const firstEnabled = days.find((day) => day.enabled);
  return days.map((day) => {
    if (day.dayOfWeek !== dayOfWeek) {
      return day;
    }
    if (day.enabled) {
      return { ...day, enabled: false };
    }
    const template = allDaysSameHour && firstEnabled ? firstEnabled : day;
    return {
      ...day,
      enabled: true,
      openTime: template.openTime || DEFAULT_OPEN_TIME,
      closeTime: template.closeTime || DEFAULT_CLOSE_TIME,
    };
  });
};

export const createDefaultStoreHoursState = (): StoreHoursFormState => ({
  days: WEEK_DAYS.map((dayOfWeek) => ({
    dayOfWeek,
    enabled: false,
    openTime: DEFAULT_OPEN_TIME,
    closeTime: DEFAULT_CLOSE_TIME,
  })),
  holiday: {
    enabled: false,
    openTime: DEFAULT_OPEN_TIME,
    closeTime: DEFAULT_CLOSE_TIME,
  },
  allDaysSameHour: false,
});
